// MySQL parseable-body fetcher (Slice 17 dispatch arm).
//
// Per the slice, MySQL exposes:
//   - views               → `information_schema.views.view_definition`
//   - scalar functions    → `information_schema.routines WHERE
//                            routine_type='FUNCTION' AND data_type!='TABLE'`
//                            (MySQL has no inline TVFs — `data_type='TABLE'`
//                            never appears for stock MySQL, but the filter
//                            stays as future-proofing.)
//   - default constraints → `information_schema.columns.column_default`
//                            (MySQL stores defaults inline; we synthesize a
//                            "<table>.<column>" name so the index can target
//                            them on rename)
//   - check constraints   → `information_schema.check_constraints`
//                            (MySQL 8.0.16+ / MariaDB 10.2.22+ — gated on a
//                            catalog-existence probe, see
//                            `probeCheckConstraintsCatalog` below)
//
// MySQL has no inline TVFs and no DDL for stand-alone DEFAULT constraints,
// so the listing's default-constraint entries piggy-back on the column
// metadata.

import type {
  ObjectKind,
  PersistedObjectListing,
  QualifiedName,
} from '../../types';

import {
  asString,
  escapeSqlLiteral,
  fetchListingRows,
  fetchRows,
  makeListing,
  type NavListingFetchOptions,
} from './queryHelper';

function listing(
  objectKind: PersistedObjectListing['objectKind'],
  schema: string,
  name: string,
  bodyText: string,
): PersistedObjectListing {
  return makeListing('mysql', objectKind, schema, name, bodyText);
}

const VIEWS_SQL = `SELECT TABLE_SCHEMA AS \`schema\`, TABLE_NAME AS name, VIEW_DEFINITION AS body
FROM information_schema.VIEWS
WHERE TABLE_SCHEMA NOT IN ('mysql', 'information_schema', 'performance_schema', 'sys')`;

const FUNCTIONS_SQL = `SELECT ROUTINE_SCHEMA AS \`schema\`, ROUTINE_NAME AS name,
  DATA_TYPE AS data_type, ROUTINE_DEFINITION AS body
FROM information_schema.ROUTINES
WHERE ROUTINE_TYPE = 'FUNCTION'
  AND ROUTINE_SCHEMA NOT IN ('mysql', 'information_schema', 'performance_schema', 'sys')`;

const DEFAULTS_SQL = `SELECT TABLE_SCHEMA AS \`schema\`, TABLE_NAME AS table_name,
  COLUMN_NAME AS column_name, COLUMN_DEFAULT AS body
FROM information_schema.COLUMNS
WHERE COLUMN_DEFAULT IS NOT NULL
  AND TABLE_SCHEMA NOT IN ('mysql', 'information_schema', 'performance_schema', 'sys')`;

// MySQL 8.0.16+ / MariaDB 10.2.22+ only — older servers have no
// information_schema.CHECK_CONSTRAINTS table at all, so the query below
// fails outright (not "zero rows") on them. `probeCheckConstraintsCatalog`
// gates this feed on a catalog-existence probe rather than a `serverVersion`
// parse: it's one tiny query, version-string agnostic, and correct for
// MariaDB's differently-numbered releases too. No row from the probe → an
// AUTHORITATIVE empty listing (this server has none to report; `onIncomplete`
// is NOT fired). A FAILED probe reports `onIncomplete` + `onFetchFailed` and
// skips the feed — it means "we don't know", not "there are none" — see the
// helper's doc
// comment. Cost: one extra round trip per listing call; `rebuildAllConstraints`
// (indexInvalidator.ts) calls the listing once per constraint DDL statement.
// Cross-engine parity: no other engine needs this gate — MSSQL
// (sys.check_constraints), PostgreSQL (pg_constraint), Oracle
// (ALL_CONSTRAINTS) and SQLite (inline DDL) expose their check-constraint
// catalog on every supported version.
const CHECKS_SQL = `SELECT CONSTRAINT_SCHEMA AS \`schema\`, CONSTRAINT_NAME AS name,
  CHECK_CLAUSE AS body
FROM information_schema.CHECK_CONSTRAINTS
WHERE CONSTRAINT_SCHEMA NOT IN ('mysql', 'information_schema', 'performance_schema', 'sys')`;

const CHECK_CONSTRAINTS_CATALOG_PROBE_SQL = `SELECT 1 AS present FROM information_schema.TABLES
WHERE TABLE_SCHEMA = 'information_schema' AND TABLE_NAME = 'CHECK_CONSTRAINTS'`;

/** Tri-state result of `probeCheckConstraintsCatalog`: `'present'` / `'absent'`
 *  are authoritative (the probe query itself ran fine); `'unknown'` means the
 *  probe query failed, so we cannot tell presence from absence. */
type CheckConstraintsCatalogProbe = 'present' | 'absent' | 'unknown';

/**
 * Does this server expose `information_schema.CHECK_CONSTRAINTS`? A FAILED
 * probe query (network blip, permission error, etc.) resolves `'unknown'`
 * rather than throwing — it means "we don't know", not "there are none", but
 * it is also not a reason to abort the whole listing. `listMysqlParseableObjects`
 * treats `'unknown'` by reporting `onIncomplete` + `onFetchFailed` and skipping
 * the checks feed (returning `[]` for it) while keeping the other three feeds'
 * rows — the same contract every other feed FAILURE in this pipeline follows
 * (`fetchListingRows` → `navPagedRows.ts`: the rows already read stay, and the
 * connection-wide build rejects rather than fulfilling readiness over them).
 * Callers (e.g. the DDL invalidator's `rebuildAllConstraints`) treat
 * `onIncomplete` as "keep my existing constraint records" instead of trusting
 * a false-empty listing.
 */
async function probeCheckConstraintsCatalog(
  options: NavListingFetchOptions,
): Promise<CheckConstraintsCatalogProbe> {
  try {
    const rows = await fetchRows(
      options.connectionId,
      options.database,
      CHECK_CONSTRAINTS_CATALOG_PROBE_SQL,
      'mysql-checks-probe',
    );
    return rows.length > 0 ? 'present' : 'absent';
  } catch {
    return 'unknown';
  }
}

// Page keys. DIVERGENCE: `information_schema` exposes no surrogate ids, so
// every feed orders by its qualified name — unique per row for views,
// routines and CHECK constraints, and `(schema, table, ordinal)` for the
// column-default feed.
export async function listMysqlParseableObjects(
  options: NavListingFetchOptions,
): Promise<readonly PersistedObjectListing[]> {
  const checksCatalogProbe = probeCheckConstraintsCatalog(options);
  const [viewRows, functionRows, defaultRows, checkRows] = await Promise.all([
    fetchListingRows(options, {
      engine: 'mysql',
      label: 'mysql-views',
      sql: VIEWS_SQL,
      orderBy: 'TABLE_SCHEMA, TABLE_NAME',
      dialect: 'limit-offset',
      bodyColumns: ['body'],
    }),
    fetchListingRows(options, {
      engine: 'mysql',
      label: 'mysql-functions',
      sql: FUNCTIONS_SQL,
      orderBy: 'ROUTINE_SCHEMA, ROUTINE_NAME',
      dialect: 'limit-offset',
      bodyColumns: ['body'],
    }),
    fetchListingRows(options, {
      engine: 'mysql',
      label: 'mysql-defaults',
      sql: DEFAULTS_SQL,
      orderBy: 'TABLE_SCHEMA, TABLE_NAME, ORDINAL_POSITION',
      dialect: 'limit-offset',
      bodyColumns: ['body'],
    }),
    // Sequenced behind the catalog probe — see `probeCheckConstraintsCatalog`
    // and the comment above `CHECKS_SQL`. The other three feeds above stay
    // concurrent with the probe itself. `'unknown'` (the probe query itself
    // failed) reports onIncomplete and skips the feed WITHOUT aborting the
    // other three feeds' rows — see the helper's doc comment.
    checksCatalogProbe.then((probe) => {
      if (probe === 'absent') return [];
      if (probe === 'unknown') {
        // The probe QUERY failed — the same class of outcome as a lost page,
        // so it raises the failure signal too and the connection-wide build
        // rejects rather than fulfilling readiness over a listing that may be
        // missing every CHECK constraint.
        options.onFetchFailed?.();
        options.onIncomplete?.();
        return [];
      }
      return fetchListingRows(options, {
        engine: 'mysql',
        label: 'mysql-checks',
        sql: CHECKS_SQL,
        orderBy: 'CONSTRAINT_SCHEMA, CONSTRAINT_NAME',
        dialect: 'limit-offset',
        bodyColumns: ['body'],
      });
    }),
  ]);

  const out: PersistedObjectListing[] = [];

  for (const row of viewRows) {
    const body = asString(row.body);
    const name = asString(row.name);
    if (!name || !body) continue;
    out.push(listing('view', asString(row.schema), name, body));
  }

  for (const row of functionRows) {
    const body = asString(row.body);
    const name = asString(row.name);
    const dataType = asString(row.data_type).toUpperCase();
    if (!name || !body) continue;
    const kind: PersistedObjectListing['objectKind'] = dataType === 'TABLE' ? 'inline-tvf' : 'scalar-function';
    out.push(listing(kind, asString(row.schema), name, body));
  }

  for (const row of defaultRows) {
    const body = asString(row.body);
    const table = asString(row.table_name);
    const column = asString(row.column_name);
    if (!table || !column || !body) continue;
    out.push(listing('default-constraint', asString(row.schema), `${table}.${column}`, body));
  }

  for (const row of checkRows) {
    const body = asString(row.body);
    const name = asString(row.name);
    if (!name || !body) continue;
    out.push(listing('check-constraint', asString(row.schema), name, body));
  }

  return out;
}

/** Schemas (MySQL databases) holding an object named `name` of any of
 *  `kinds` — the DDL invalidator's "where did this bare statement run?"
 *  probe. `information_schema` spans every schema the login can see. */
export async function listMysqlSchemasHoldingObject(
  connectionId: string,
  database: string,
  name: string,
  kinds: readonly ObjectKind[],
): Promise<readonly string[]> {
  const n = escapeSqlLiteral(name);
  const parts: string[] = [];
  if (kinds.includes('view')) {
    parts.push(`SELECT TABLE_SCHEMA AS schema_name FROM information_schema.VIEWS WHERE TABLE_NAME = '${n}'`);
  }
  const routineTypes = [
    ...(kinds.includes('inline-tvf') || kinds.includes('scalar-function') ? ["'FUNCTION'"] : []),
    ...(kinds.includes('procedure') ? ["'PROCEDURE'"] : []),
  ];
  if (routineTypes.length > 0) {
    parts.push(`SELECT ROUTINE_SCHEMA AS schema_name FROM information_schema.ROUTINES
      WHERE ROUTINE_NAME = '${n}' AND ROUTINE_TYPE IN (${routineTypes.join(', ')})`);
  }
  if (kinds.includes('trigger')) {
    parts.push(`SELECT TRIGGER_SCHEMA AS schema_name FROM information_schema.TRIGGERS WHERE TRIGGER_NAME = '${n}'`);
  }
  if (parts.length === 0) return [];
  const sql = `SELECT DISTINCT schema_name AS \`schema\` FROM (${parts.join('\n      UNION ')}) AS held`;
  const rows = await fetchRows(connectionId, database, sql, 'mysql-schemas-of');
  return rows.map((r) => asString(r.schema)).filter((s) => s.length > 0);
}

export async function fetchMysqlObjectBody(
  connectionId: string,
  database: string,
  objectKind: ObjectKind,
  objectName: QualifiedName,
): Promise<string | null> {
  const schema = objectName.schema ?? database;
  const s = escapeSqlLiteral(schema);
  const n = escapeSqlLiteral(objectName.name);

  if (objectKind === 'view') {
    const sql = `SELECT VIEW_DEFINITION AS body FROM information_schema.VIEWS
      WHERE TABLE_SCHEMA = '${s}' AND TABLE_NAME = '${n}'`;
    const rows = await fetchRows(connectionId, database, sql, 'mysql-view');
    return asString(rows[0]?.body) || null;
  }

  if (objectKind === 'inline-tvf' || objectKind === 'scalar-function') {
    // Same kind rule as the listing (`DATA_TYPE = 'TABLE'` → inline-tvf): the
    // DDL invalidator asks for the name under BOTH kinds, and only the kind
    // the listing would file it under may answer.
    const sql = `SELECT ROUTINE_DEFINITION AS body, DATA_TYPE AS data_type FROM information_schema.ROUTINES
      WHERE ROUTINE_SCHEMA = '${s}' AND ROUTINE_NAME = '${n}' AND ROUTINE_TYPE = 'FUNCTION'`;
    const rows = await fetchRows(connectionId, database, sql, 'mysql-function');
    const row = rows[0];
    if (!row) return null;
    const kind = asString(row.data_type).toUpperCase() === 'TABLE' ? 'inline-tvf' : 'scalar-function';
    if (kind !== objectKind) return null;
    return asString(row.body) || null;
  }

  if (objectKind === 'check-constraint') {
    const sql = `SELECT CHECK_CLAUSE AS body FROM information_schema.CHECK_CONSTRAINTS
      WHERE CONSTRAINT_SCHEMA = '${s}' AND CONSTRAINT_NAME = '${n}'`;
    const rows = await fetchRows(connectionId, database, sql, 'mysql-check');
    return asString(rows[0]?.body) || null;
  }

  if (objectKind === 'default-constraint') {
    const dot = objectName.name.lastIndexOf('.');
    if (dot < 0) return null;
    const table = escapeSqlLiteral(objectName.name.slice(0, dot));
    const column = escapeSqlLiteral(objectName.name.slice(dot + 1));
    const sql = `SELECT COLUMN_DEFAULT AS body FROM information_schema.COLUMNS
      WHERE TABLE_SCHEMA = '${s}' AND TABLE_NAME = '${table}' AND COLUMN_NAME = '${column}'`;
    const rows = await fetchRows(connectionId, database, sql, 'mysql-default');
    return asString(rows[0]?.body) || null;
  }

  return null;
}

// Procedural feed (ProceduralBodyIndexing Slice 3.1). Enumerates standalone
// stored procedures (information_schema.ROUTINES filtered by
// ROUTINE_TYPE = 'PROCEDURE') + triggers (information_schema.TRIGGERS).
// Bodies come back as the inner statement / compound block (no
// CREATE PROCEDURE wrapper). MySQL routine bodies are usually wrapped in
// `BEGIN ... END`, which the universal scanner classifies as
// `transaction-control` — we strip the outer keywords so the inner
// `;`-separated statements surface as `kind === 'select'`. See the
// pgsqlProceduralFetcher's `stripPgProceduralWrapper` for the same pattern.
export {
  listMysqlProceduralObjects,
  fetchMysqlProceduralObjectBody,
} from './mysqlProceduralFetcher';
